import React from 'react';
import {
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Box,
	Typography,
} from '@mui/material';
import { useQuery, gql } from '@apollo/client';
import { ProductInput } from '../../gql/graphql';
import { useShopDetails } from '../../hooks/use-shop';

const GET_PRODUCT_CATEGORIES = gql`
	query getProductCategories {
		productCategories {
			id
			name
		}
	}
`;

interface ProductCategoryOption {
	id: number;
	name: string;
}

interface BasicInfoTabProps {
	productData: Partial<ProductInput>;
	updateProductData: (updates: Partial<ProductInput>) => void;
}

function BasicInfoTab({ productData, updateProductData }: BasicInfoTabProps) {
	const { shopId, locationId } = useShopDetails();
	const { data: categoryData, loading: categoriesLoading } = useQuery(GET_PRODUCT_CATEGORIES);
	const categories: ProductCategoryOption[] = categoryData?.productCategories ?? [];

	// Initialize shopId and locationId if not set
	React.useEffect(() => {
		if (!productData.shopId) {
			updateProductData({ shopId, locationId });
		}
	}, []);

	const handleChange = (field: keyof ProductInput) => (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
	) => {
		updateProductData({ [field]: event.target.value });
	};

	const handleTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const tags = event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean);
		updateProductData({ tags });
	};

	// The stored category may have been deactivated or deleted since. Falling
	// back to '' keeps MUI in controlled mode instead of warning about a value
	// with no matching option.
	const selectedCategory =
		productData.categoryId && categories.some((category) => category.id === productData.categoryId)
			? String(productData.categoryId)
			: '';

	const categoryHelperText = categoriesLoading
		? 'Loading categories…'
		: categories.length === 0
			? 'No categories yet. Add them under product categories first.'
			: 'Groups the product in the customer app';

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Basic Product Information
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Enter the core details about your product
			</Typography>

			<Grid container spacing={3}>
				{/* Product Name */}
				<Grid item xs={12} md={8}>
					<TextField
						fullWidth
						required
						label="Product Name"
						name="name"
						value={productData.name || ''}
						onChange={handleChange('name')}
						helperText="Enter a clear, descriptive product name"
					/>
				</Grid>

				{/* SKU */}
				<Grid item xs={12} md={4}>
					<TextField
						fullWidth
						label="SKU"
						name="sku"
						value={productData.sku || ''}
						onChange={handleChange('sku')}
						helperText="Stock Keeping Unit (optional)"
					/>
				</Grid>

				{/* Category */}
				<Grid item xs={12} md={6}>
					<FormControl fullWidth disabled={categoriesLoading || categories.length === 0}>
						<InputLabel>Category</InputLabel>
						<Select
							label="Category"
							name="categoryId"
							value={selectedCategory}
							onChange={(event) => {
								const value = event.target.value;
								updateProductData({ categoryId: value === '' ? null : Number(value) });
							}}
						>
							<MenuItem value="">
								<em>No category</em>
							</MenuItem>
							{categories.map((category) => (
								<MenuItem key={category.id} value={String(category.id)}>
									{category.name}
								</MenuItem>
							))}
						</Select>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
							{categoryHelperText}
						</Typography>
					</FormControl>
				</Grid>

				{/* Tags */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Tags"
						name="tags"
						value={productData.tags?.join(', ') || ''}
						onChange={handleTagsChange}
						helperText="Comma-separated tags (e.g., organic, premium, new)"
					/>
					{productData.tags && productData.tags.length > 0 && (
						<Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
							{productData.tags.map((tag, index) => (
								<Chip key={index} label={tag} size="small" />
							))}
						</Box>
					)}
				</Grid>

				{/* Description */}
				<Grid item xs={12}>
					<TextField
						fullWidth
						multiline
						rows={4}
						label="Description"
						name="description"
						value={productData.description || ''}
						onChange={handleChange('description')}
						helperText="Detailed product description"
					/>
				</Grid>

				{/* Images */}
				<Grid item xs={12}>
					<TextField
						fullWidth
						label="Image URLs"
						name="images"
						value={productData.images?.join(', ') || ''}
						onChange={(e) => {
							const images = e.target.value
								.split(',')
								.map((url) => url.trim())
								.filter(Boolean);
							updateProductData({ images });
						}}
						helperText="Comma-separated image URLs"
					/>
				</Grid>
			</Grid>
		</Box>
	);
}

export default BasicInfoTab;
